import { useEffect, useState } from "react"

import { useLocation, useSearchParams } from "react-router-dom"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"
import { DictType } from "@pma/types"
import useTranslator from "@hooks/use-translator"
import { useAuth } from "@stores/auth-store"
import {
  formatTime,
  getUserFromId,
  parseMessage,
  formatDate_he,
  formatDate,
  cn,
} from "@pma/utils/helpers"
import { format, formatDistance } from "date-fns"
import { he } from "date-fns/locale"

import { Card } from "@components/common/card"
import NoteActions from "./note-actions"
import NoteTextArea from "./note-text-area"
import NoteMenuOptions from "./note-menu"
import AttachFileToNote from "../projects/notes-attached-files"

const Note = ({
  message,
  messages,
  setSelectedNotes,
  selectedNotes,
  fromExpandCalling = false,
}: any) => {
  const [showReply, setShowReply] = useState(false)
  const [author, setAuthor] = useState("")
  const { locale } = useTranslator()
  const { authConfig } = useAuth()
  const [isChecked, setIsChecked] = useState(false)

  const isHebrew = locale === "he"

  const [searchParam] = useSearchParams()

  const queryKey = searchParam.get("revalidate")

  const organizationUsers = authConfig?.organizationUsers

  const sortMessages = (messages: Array<any>) => {
    messages.sort(
      (a: DictType, b: DictType) =>
        parseInt(String(new Date(a?.created_at))) - parseInt(String(new Date(b?.created_at))),
    )

    return [...messages]
  }
  const messageReplies = sortMessages(
    messages.filter((reply: DictType) => reply.reply_to_note === message.id),
  )

  const getUser = (userId: string) => {
    let user
    const fromOptions = getUserFromId(userId, organizationUsers)
    const loggedInUserId = authConfig?.id

    if (loggedInUserId === userId) {
      user = authConfig
    } else {
      user === fromOptions
    }

    return user
  }

  useEffect(() => {
    setAuthor(getUser(message?.user_id)?.name)
  }, [messages])

  useEffect(() => {
    if (queryKey === "tasks") {
      setShowReply(false)
    }
  }, [queryKey])

  const handleCheckbox = (e: any) => {
    const { name, checked } = e.target
    setIsChecked(checked)

    const arr = [...selectedNotes]

    const found = arr.find((val) => val.id === name)

    if (found) {
      const index = arr.indexOf(found)
      if (checked) {
        arr[index]["checked"] = checked
      } else {
        arr.splice(index, 1)
      }
    } else {
      arr.push({ id: name, checked })
    }
    setSelectedNotes(arr)
  }

  return (
    <Card className='my-2 max-w-64 p-3 md:max-w-[335px]'>
      <div>
        <div className={cn("flex w-full items-center justify-between", isHebrew ? "rtl" : "ltr")}>
          <Tooltip>
            <TooltipTrigger className=''>
              <div className='flex items-center'>
                <img
                  alt=''
                  src={
                    message?.user?.user_picture
                      ? message.user.user_picture.url
                      : `${process.env.PUBLIC_URL}/usr.svg`
                  }
                  className='h-8 w-8 rounded-full'
                />

                <strong className='px-2 capitalize'>
                  <small>
                    {getUserFromId(message?.user_id, organizationUsers)?.organization_name}
                  </small>
                </strong>
              </div>
            </TooltipTrigger>
            <TooltipContent className='flex items-center gap-1'>
              <h4>{message.user?.name}</h4>
            </TooltipContent>
          </Tooltip>

          <div className='flex'>
            <input
              type='checkbox'
              name={message.id}
              checked={isChecked}
              onChange={handleCheckbox}
            />

            <NoteMenuOptions note={message} fromExpandCalling={fromExpandCalling} />
          </div>
        </div>
      </div>

      <p
        dangerouslySetInnerHTML={{
          __html: parseMessage(message?.note, organizationUsers),
        }}
        className={cn(
          "allow-list max-w-64 p-2 ps-3 text-left text-xs text-slate-500 md:max-w-[350px]",
          isHebrew ? "text-right" : "text-left",
        )}
      />

      <div
        className={cn(
          "hidden w-full flex-col justify-start text-wrap md:flex",
          isHebrew ? "rtl" : "ltr",
        )}
      >
        <AttachFileToNote noteId={message?.id} files={message?._files} size='sm' />
      </div>
      {/* Note time info */}
      <div className='flex w-full items-center justify-end gap-0.5'>
        <Tooltip>
          <TooltipTrigger className='m-0'>
            <small
              className='message-date text-[9px]'
              data-pr-position='top'
              data-pr-tooltip={` ${format(message?.created_at, "dd MMM", {
                locale: isHebrew ? he : undefined,
              })} at ${formatTime(message?.created_at, { amPm: true })}`}
            >
              {formatDistance(message?.created_at, new Date(), {
                locale: isHebrew ? he : undefined,
                addSuffix: true,
              })}
            </small>
          </TooltipTrigger>
          <TooltipContent>
            <span className='w-fit text-xs'>
              {isHebrew ? formatDate_he(message.created_at) : formatDate(message.created_at)}
            </span>
          </TooltipContent>
        </Tooltip>
      </div>

      <div className='my-2 flex w-full flex-col gap-4'>
        {/* NOTES OPTIONS */}
        <NoteActions note={message} showReply={setShowReply} likes={message?.liked_by} />

        {/* Note Reply */}
        {showReply && (
          <NoteTextArea
            task={message}
            reply
            setShowReply={setShowReply}
            focus
            fromExpandCalling={fromExpandCalling}
          />
        )}
      </div>
      <div className='my-2 flex w-full flex-col gap-4'>
        {messageReplies.map((reply: any) => (
          <div className='flex gap-2' key={reply?.id}>
            <div className=''>
              <img
                className='h-6 w-6 rounded-full'
                src={
                  reply?.user?.user_picture
                    ? reply.user.user_picture.url
                    : `${process.env.PUBLIC_URL}/usr.svg`
                }
              />
            </div>
            <div
              className={`flex min-h-12 w-fit min-w-[250px] flex-col items-start justify-center rounded-3xl border border-slate-400 bg-blue-300/20 p-2 pb-3 transition-all ease-in-out hover:scale-105 hover:bg-blue-300/40`}
            >
              <strong className='px-2 capitalize'>
                <small>{reply?.user?.name}</small>
              </strong>

              <p
                dangerouslySetInnerHTML={{
                  __html: parseMessage(reply?.note, organizationUsers),
                }}
                className='max-w-[330px] p-2 text-left text-xs text-slate-500 md:max-w-[350px]'
              />

              <div className='flex w-full justify-between'>
                <div className='flex hidden w-full flex-wrap justify-start md:flex'>
                  <AttachFileToNote
                    showAsFolder={true}
                    noteId={reply?.id}
                    files={reply?._files}
                    size='sm'
                  />
                </div>
                <small className='message-date mt-1 text-[9px]'>
                  {formatDistance(reply?.created_at, new Date(), {
                    locale: isHebrew ? he : undefined,
                    addSuffix: true,
                  })}
                </small>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  )
}

export default Note
